// components/superAdmin/SuperAdminAccountsTab.js
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Drawer,
  Tabs,
  Tab,
  Checkbox,
  LinearProgress,
  InputAdornment,
  MenuItem,
  Tooltip,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch } from 'react-redux';
import { createAccount, updateAccount, deleteAccount, bulkUpdateAccounts } from '../../slices/superAdminSlice';
import { useToast } from '../../context/ToastContext';
import { FEATURE_GROUPS, FEATURE_FLAGS, MODEL_FEATURE_DEFAULTS, accountStatus, addDaysISO, daysUntil } from './featureFlags';
import { SA, cardSx } from './theme';

const FILTERS = [
  { key: 'all', label: 'الكل' },
  { key: 'active', label: 'نشطة' },
  { key: 'expiring', label: 'قريبة الانتهاء' },
  { key: 'expired', label: 'منتهية' },
  { key: 'suspended', label: 'موقوفة' },
];

const buildEmptyForm = (settings) => ({
  name: '', school_name: '', phone_number: '', email: '', address: '',
  contact_person: '', admin_notes: '',
  max_students: settings?.default_max_students ?? 100,
  max_users: settings?.default_max_users ?? '',
  subscription_end_date: settings?.default_trial_days ? addDaysISO(settings.default_trial_days) : '',
  is_active: true,
  ...MODEL_FEATURE_DEFAULTS,
  ...(settings?.default_features || {}),
});

// Strip read-only fields and turn empty optional inputs into nulls
const toPayload = (form) => {
  const payload = {
    name: form.name, school_name: form.school_name, phone_number: form.phone_number,
    email: form.email || null, address: form.address,
    contact_person: form.contact_person, admin_notes: form.admin_notes,
    max_students: Number(form.max_students) || 0,
    max_users: form.max_users === '' || form.max_users === null ? null : Number(form.max_users),
    subscription_end_date: form.subscription_end_date || null,
    is_active: !!form.is_active,
  };
  FEATURE_FLAGS.forEach((f) => { payload[f.key] = !!form[f.key]; });
  return payload;
};

const firstError = (err) => {
  if (!err || typeof err !== 'object') return null;
  if (err.detail || err.error) return err.detail || err.error;
  const first = Object.values(err)[0];
  return Array.isArray(first) ? first[0] : null;
};

const UsageBar = ({ value, max }) => {
  const pct = max ? Math.min(100, (value / max) * 100) : 0;
  const color = pct >= 90 ? SA.danger : pct >= 75 ? SA.warnInk : SA.primary;
  return (
    <Box sx={{ minWidth: 110 }}>
      <Typography sx={{ fontSize: 13, color: SA.text, fontFamily: SA.font }}>
        {value}<Typography component="span" sx={{ color: SA.muted, fontSize: 12 }}> / {max ?? '∞'}</Typography>
      </Typography>
      {max ? (
        <LinearProgress
          variant="determinate" value={pct}
          sx={{ mt: 0.5, height: 4, borderRadius: 2, bgcolor: SA.border, '& .MuiLinearProgress-bar': { bgcolor: color } }}
        />
      ) : null}
    </Box>
  );
};

const Section = ({ title, children, action }) => (
  <Box sx={{ mb: 3 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
      <Typography sx={{ fontWeight: 800, color: SA.text, fontSize: 15, flexGrow: 1, fontFamily: SA.font }}>{title}</Typography>
      {action}
    </Box>
    {children}
  </Box>
);

const SuperAdminAccountsTab = ({ accounts, settings, intent, onRefresh }) => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const warningDays = settings?.expiry_warning_days ?? 14;
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState([]);
  const [bulkFeature, setBulkFeature] = useState('');
  const [editing, setEditing] = useState(null); // account object or 'new'
  const [drawerTab, setDrawerTab] = useState(0);
  const [form, setForm] = useState(() => buildEmptyForm(settings));
  const [saving, setSaving] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const openEdit = (account) => {
    setForm(account === 'new' ? buildEmptyForm(settings) : { ...buildEmptyForm(settings), ...account, max_users: account.max_users ?? '', subscription_end_date: account.subscription_end_date || '' });
    setDrawerTab(0);
    setEditing(account);
  };

  // Overview → "open this school" / "show expiring schools"
  useEffect(() => {
    if (!intent) return;
    if (intent.filter) setFilter(intent.filter);
    if (intent.openId) {
      const acc = accounts.find((a) => a.id === intent.openId);
      if (acc) openEdit(acc);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intent]);

  const withStatus = useMemo(
    () => accounts.map((acc) => ({ acc, status: accountStatus(acc, warningDays) })),
    [accounts, warningDays]
  );

  const counts = useMemo(() => {
    const c = { all: withStatus.length };
    withStatus.forEach(({ status }) => { c[status.key] = (c[status.key] || 0) + 1; });
    return c;
  }, [withStatus]);

  const rows = withStatus.filter(({ acc, status }) => {
    const term = searchTerm.trim().toLowerCase();
    const matchesTerm = !term || [acc.name, acc.school_name, acc.email, acc.phone_number, acc.contact_person]
      .some((v) => v?.toLowerCase().includes(term));
    return matchesTerm && (filter === 'all' || status.key === filter);
  });

  const allSelected = rows.length > 0 && rows.every(({ acc }) => selected.includes(acc.id));
  const toggleAll = () => setSelected(allSelected ? [] : rows.map(({ acc }) => acc.id));
  const toggleOne = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const handleSave = async () => {
    if (!form.name && !form.school_name) {
      showToast('اسم المدرسة مطلوب', 'error');
      setDrawerTab(0);
      return;
    }
    setSaving(true);
    try {
      if (editing === 'new') {
        await dispatch(createAccount(toPayload(form))).unwrap();
        showToast('تم إنشاء المدرسة بنجاح', 'success');
      } else {
        await dispatch(updateAccount({ id: editing.id, ...toPayload(form) })).unwrap();
        showToast('تم حفظ التغييرات', 'success');
      }
      setEditing(null);
      onRefresh();
    } catch (error) {
      showToast(firstError(error) || 'حدث خطأ أثناء الحفظ', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleQuickToggle = async (acc) => {
    try {
      await dispatch(updateAccount({ id: acc.id, is_active: !acc.is_active })).unwrap();
      showToast(acc.is_active ? 'تم إيقاف المدرسة' : 'تم تفعيل المدرسة', 'success');
    } catch (error) {
      showToast(firstError(error) || 'حدث خطأ', 'error');
    }
  };

  const runBulk = async (changes, message) => {
    try {
      await dispatch(bulkUpdateAccounts({ ids: selected, changes })).unwrap();
      showToast(message, 'success');
      setSelected([]);
      setBulkFeature('');
      onRefresh();
    } catch (error) {
      showToast(firstError(error) || 'حدث خطأ', 'error');
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog) return;
    try {
      await dispatch(deleteAccount(deleteDialog.id)).unwrap();
      showToast('تم حذف المدرسة بنجاح', 'success');
      setDeleteDialog(null);
      setDeleteConfirmText('');
      onRefresh();
    } catch (error) {
      showToast(firstError(error) || 'حدث خطأ أثناء الحذف', 'error');
    }
  };

  const deleteName = deleteDialog ? (deleteDialog.school_name || deleteDialog.name || '') : '';
  const enabledCount = FEATURE_FLAGS.filter((f) => form[f.key]).length;
  const subLeft = daysUntil(form.subscription_end_date);

  return (
    <Box>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
        <TextField
          placeholder="ابحث بالاسم، البريد، الهاتف..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          sx={{ minWidth: 260, bgcolor: '#fff' }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          {FILTERS.map((f) => (
            <Chip
              key={f.key}
              label={`${f.label} ${counts[f.key] || 0}`}
              onClick={() => setFilter(f.key)}
              color={filter === f.key ? 'primary' : 'default'}
              variant={filter === f.key ? 'filled' : 'outlined'}
              sx={{ bgcolor: filter === f.key ? undefined : '#fff' }}
            />
          ))}
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <Button startIcon={<AddIcon />} variant="contained" disableElevation onClick={() => openEdit('new')} sx={{ borderRadius: 2, bgcolor: SA.primary }}>
          مدرسة جديدة
        </Button>
      </Box>

      {/* Bulk action bar */}
      {selected.length > 0 && (
        <Box sx={{ ...cardSx, bgcolor: SA.primarySoft, borderColor: SA.primaryLight, px: 2, py: 1, mb: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography sx={{ fontWeight: 700, color: SA.primary, fontFamily: SA.font }}>تم تحديد {selected.length}</Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Button size="small" onClick={() => runBulk({ is_active: true }, 'تم تفعيل المدارس المحددة')}>تفعيل</Button>
          <Button size="small" color="warning" onClick={() => runBulk({ is_active: false }, 'تم إيقاف المدارس المحددة')}>إيقاف</Button>
          <TextField
            select size="small" value={bulkFeature} onChange={(e) => setBulkFeature(e.target.value)}
            sx={{ minWidth: 180, bgcolor: '#fff' }} SelectProps={{ displayEmpty: true }}
          >
            <MenuItem value="" disabled>اختر ميزة...</MenuItem>
            {FEATURE_FLAGS.map((f) => <MenuItem key={f.key} value={f.key}>{f.label}</MenuItem>)}
          </TextField>
          <Button size="small" disabled={!bulkFeature} onClick={() => runBulk({ [bulkFeature]: true }, 'تم تفعيل الميزة')}>تفعيل الميزة</Button>
          <Button size="small" disabled={!bulkFeature} color="inherit" onClick={() => runBulk({ [bulkFeature]: false }, 'تم إخفاء الميزة')}>إخفاء الميزة</Button>
          <IconButton size="small" onClick={() => setSelected([])}><CloseIcon fontSize="small" /></IconButton>
        </Box>
      )}

      {/* Table */}
      <TableContainer sx={cardSx}>
        <Table size="small" sx={{ '& td, & th': { fontFamily: SA.font, borderColor: SA.border }, '& th': { color: SA.muted, fontWeight: 700, fontSize: 12.5, bgcolor: '#FAFBFD' }, '& td': { py: 1.25 } }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox"><Checkbox size="small" checked={allSelected} indeterminate={!allSelected && selected.length > 0} onChange={toggleAll} /></TableCell>
              <TableCell>المدرسة</TableCell>
              <TableCell>الطلاب</TableCell>
              <TableCell>المستخدمون</TableCell>
              <TableCell>الاشتراك</TableCell>
              <TableCell>آخر نشاط</TableCell>
              <TableCell align="center">نشطة</TableCell>
              <TableCell align="center" />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py: 6, color: SA.muted }}>لا توجد مدارس مطابقة</TableCell></TableRow>
            )}
            {rows.map(({ acc, status }) => (
              <TableRow key={acc.id} hover selected={selected.includes(acc.id)} sx={{ opacity: acc.is_active ? 1 : 0.6 }}>
                <TableCell padding="checkbox"><Checkbox size="small" checked={selected.includes(acc.id)} onChange={() => toggleOne(acc.id)} /></TableCell>
                <TableCell sx={{ cursor: 'pointer' }} onClick={() => openEdit(acc)}>
                  <Typography sx={{ fontWeight: 700, color: SA.text, fontSize: 14, fontFamily: SA.font }}>{acc.school_name || acc.name || '—'}</Typography>
                  <Typography sx={{ color: SA.muted, fontSize: 12, fontFamily: SA.font }}>{acc.contact_person || acc.email || acc.phone_number || ''}</Typography>
                </TableCell>
                <TableCell><UsageBar value={acc.student_count} max={acc.max_students} /></TableCell>
                <TableCell><UsageBar value={acc.user_count} max={acc.max_users} /></TableCell>
                <TableCell>
                  <Chip size="small" label={status.label} color={status.color} variant={status.key === 'active' ? 'outlined' : 'filled'} />
                  {acc.subscription_end_date && (
                    <Typography sx={{ color: SA.muted, fontSize: 11.5, mt: 0.25, fontFamily: SA.font }}>حتى {acc.subscription_end_date}</Typography>
                  )}
                </TableCell>
                <TableCell sx={{ color: SA.muted, fontSize: 12.5 }}>
                  {acc.last_activity ? new Date(acc.last_activity).toLocaleDateString('ar-EG') : 'لا يوجد'}
                </TableCell>
                <TableCell align="center">
                  <Switch size="small" checked={!!acc.is_active} onChange={() => handleQuickToggle(acc)} />
                </TableCell>
                <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                  <Tooltip title="تعديل"><IconButton size="small" onClick={() => openEdit(acc)}><EditOutlinedIcon fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="حذف"><IconButton size="small" color="error" onClick={() => setDeleteDialog(acc)}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create / edit drawer */}
      <Drawer
        anchor="left"
        open={!!editing}
        onClose={() => setEditing(null)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 520 }, direction: 'rtl', fontFamily: SA.font, display: 'flex', flexDirection: 'column' } }}
      >
        <Box sx={{ px: 3, pt: 2.5, pb: 1, display: 'flex', alignItems: 'center' }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography sx={{ fontSize: 20, fontWeight: 800, color: SA.text, fontFamily: SA.font }}>
              {editing === 'new' ? 'مدرسة جديدة' : (form.school_name || form.name)}
            </Typography>
            {editing !== 'new' && editing && (
              <Typography sx={{ fontSize: 12.5, color: SA.muted, fontFamily: SA.font }}>
                {editing.student_count} طالب · {editing.user_count} مستخدم
              </Typography>
            )}
          </Box>
          <IconButton onClick={() => setEditing(null)}><CloseIcon /></IconButton>
        </Box>
        <Tabs value={drawerTab} onChange={(e, v) => setDrawerTab(v)} variant="fullWidth" sx={{ borderBottom: `1px solid ${SA.border}`, '& .MuiTab-root': { fontFamily: SA.font, fontWeight: 700 } }}>
          <Tab label="البيانات" />
          <Tab label="الاشتراك والحدود" />
          <Tab label={`الميزات (${enabledCount}/${FEATURE_FLAGS.length})`} />
        </Tabs>

        <Box sx={{ p: 3, flexGrow: 1, overflowY: 'auto' }}>
          {drawerTab === 0 && (
            <Box sx={{ display: 'grid', gap: 2 }}>
              <TextField fullWidth label="اسم المدرسة" required value={form.school_name || ''} onChange={(e) => set({ school_name: e.target.value })} />
              <TextField fullWidth label="اسم الحساب" value={form.name || ''} onChange={(e) => set({ name: e.target.value })} />
              <TextField fullWidth label="الشخص المسؤول" value={form.contact_person || ''} onChange={(e) => set({ contact_person: e.target.value })} />
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <TextField fullWidth label="البريد الإلكتروني" value={form.email || ''} onChange={(e) => set({ email: e.target.value })} />
                <TextField fullWidth label="الهاتف" value={form.phone_number || ''} onChange={(e) => set({ phone_number: e.target.value })} />
              </Box>
              <TextField fullWidth label="العنوان" value={form.address || ''} onChange={(e) => set({ address: e.target.value })} />
              <TextField
                fullWidth multiline minRows={3} label="ملاحظات داخلية"
                helperText="تظهر لمسؤول المنصة فقط"
                value={form.admin_notes || ''} onChange={(e) => set({ admin_notes: e.target.value })}
              />
            </Box>
          )}

          {drawerTab === 1 && (
            <Box>
              <Section title="حالة الحساب">
                <Box sx={{ ...cardSx, p: 2, display: 'flex', alignItems: 'center', bgcolor: form.is_active ? SA.successSoft : SA.dangerSoft, borderColor: 'transparent' }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontFamily: SA.font }}>{form.is_active ? 'المدرسة نشطة' : 'المدرسة موقوفة'}</Typography>
                    <Typography sx={{ fontSize: 12.5, color: SA.muted, fontFamily: SA.font }}>
                      {form.is_active ? 'يستطيع المستخدمون تسجيل الدخول' : 'جميع مستخدمي المدرسة ممنوعون من الدخول'}
                    </Typography>
                  </Box>
                  <Switch checked={!!form.is_active} onChange={(e) => set({ is_active: e.target.checked })} color="success" />
                </Box>
              </Section>

              <Section title="الاشتراك">
                <TextField
                  fullWidth type="date" label="تاريخ انتهاء الاشتراك"
                  InputLabelProps={{ shrink: true }}
                  value={form.subscription_end_date || ''}
                  onChange={(e) => set({ subscription_end_date: e.target.value })}
                  helperText={
                    !form.subscription_end_date ? 'بدون تاريخ = اشتراك مفتوح'
                      : subLeft < 0 ? `منتهي منذ ${-subLeft} يوم — الدخول ممنوع`
                      : `متبقي ${subLeft} يوم`
                  }
                  FormHelperTextProps={{ sx: { color: subLeft !== null && subLeft < 0 ? SA.danger : undefined } }}
                />
                <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                  {[{ d: 30, l: '+ شهر' }, { d: 90, l: '+ 3 أشهر' }, { d: 365, l: '+ سنة' }].map(({ d, l }) => (
                    <Chip
                      key={d} label={l} variant="outlined" onClick={() => {
                        // Extend from the later of today / current end date
                        const base = form.subscription_end_date && subLeft > 0 ? subLeft : 0;
                        set({ subscription_end_date: addDaysISO(base + d) });
                      }}
                    />
                  ))}
                  <Chip label="بدون انتهاء" variant="outlined" onClick={() => set({ subscription_end_date: '' })} />
                </Box>
              </Section>

              <Section title="الحدود">
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <TextField
                    fullWidth type="number" label="الحد الأقصى للطلاب"
                    value={form.max_students ?? ''} onChange={(e) => set({ max_students: e.target.value })}
                    inputProps={{ min: 0 }}
                  />
                  <TextField
                    fullWidth type="number" label="الحد الأقصى للمستخدمين"
                    placeholder="غير محدود" helperText="فارغ = غير محدود"
                    value={form.max_users ?? ''} onChange={(e) => set({ max_users: e.target.value })}
                    inputProps={{ min: 0 }}
                  />
                </Box>
              </Section>
            </Box>
          )}

          {drawerTab === 2 && (
            <Box>
              <Typography sx={{ fontSize: 13, color: SA.muted, mb: 2, fontFamily: SA.font }}>
                الأقسام التي تظهر في قائمة المدرسة. إخفاء القسم يخفيه لجميع مستخدمي المدرسة.
              </Typography>
              {FEATURE_GROUPS.map((group) => {
                const on = group.flags.filter((f) => form[f.key]).length;
                const allOn = on === group.flags.length;
                return (
                  <Section
                    key={group.key}
                    title={`${group.label} (${on}/${group.flags.length})`}
                    action={
                      <Button size="small" onClick={() => set(Object.fromEntries(group.flags.map((f) => [f.key, !allOn])))}>
                        {allOn ? 'إخفاء الكل' : 'إظهار الكل'}
                      </Button>
                    }
                  >
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                      {group.flags.map((flag) => (
                        <Box
                          key={flag.key}
                          onClick={() => set({ [flag.key]: !form[flag.key] })}
                          sx={{
                            ...cardSx, borderRadius: 2, px: 1.5, py: 0.75, display: 'flex', alignItems: 'center', cursor: 'pointer',
                            borderColor: form[flag.key] ? SA.primaryLight : SA.border,
                            bgcolor: form[flag.key] ? SA.primarySoft : '#fff',
                          }}
                        >
                          <Typography sx={{ fontSize: 13.5, flexGrow: 1, fontFamily: SA.font, color: form[flag.key] ? SA.text : SA.muted }}>{flag.label}</Typography>
                          <Switch size="small" checked={!!form[flag.key]} onClick={(e) => e.stopPropagation()} onChange={(e) => set({ [flag.key]: e.target.checked })} />
                        </Box>
                      ))}
                    </Box>
                  </Section>
                );
              })}
            </Box>
          )}
        </Box>

        <Box sx={{ p: 2, borderTop: `1px solid ${SA.border}`, display: 'flex', gap: 1 }}>
          <Button variant="contained" disableElevation onClick={handleSave} disabled={saving} sx={{ flexGrow: 1, borderRadius: 2, bgcolor: SA.primary }}>
            {editing === 'new' ? 'إنشاء المدرسة' : 'حفظ التغييرات'}
          </Button>
          <Button onClick={() => setEditing(null)} color="inherit">إلغاء</Button>
        </Box>
      </Drawer>

      {/* Critical action: delete confirmation (type the name) */}
      <Dialog open={!!deleteDialog} onClose={() => { setDeleteDialog(null); setDeleteConfirmText(''); }} PaperProps={{ sx: { direction: 'rtl' } }}>
        <DialogTitle sx={{ fontFamily: SA.font, fontWeight: 800 }}>حذف المدرسة نهائياً</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: SA.font, mb: 2 }}>
            سيتم حذف جميع بيانات <b>{deleteName}</b> بما في ذلك المستخدمين والطلاب. لا يمكن التراجع عن هذا الإجراء.
            إن أردت فقط منع الدخول، استخدم <b>الإيقاف</b> بدلاً من الحذف.
          </Typography>
          <TextField
            fullWidth size="small" autoFocus
            label={`اكتب "${deleteName}" للتأكيد`}
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDeleteDialog(null); setDeleteConfirmText(''); }}>إلغاء</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disableElevation disabled={deleteConfirmText.trim() !== deleteName.trim()}>
            حذف نهائياً
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SuperAdminAccountsTab;
